import { createCloudRunAxios } from './axios';

const remiApi = createCloudRunAxios('remi-api');

type SendgridSetupResponse = {
  success?: boolean;
  partial_success?: boolean;
  error?: string;
  message?: string;
};

type ApiErrorPayload = {
  response?: { data?: { detail?: string; error?: string; message?: string } };
};

const assertSetupSucceeded = <T extends SendgridSetupResponse>(result: T) => {
  if (result.success === false || result.partial_success) {
    throw new Error(
      result?.error ||
        result?.message ||
        'Email domain setup did not complete. Please check SendGrid and retry.'
    );
  }
  return result;
};

const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error) return error.message;

  const maybeResponse = error as ApiErrorPayload;
  return (
    maybeResponse.response?.data?.error ||
    maybeResponse.response?.data?.message ||
    maybeResponse.response?.data?.detail ||
    fallback
  );
};

export interface OrganizationSetupRequest {
  organization_id: string;
  email_domain?: string;
  reply_to_email?: string;
  reply_to_name?: string;
}

export interface OrganizationUpdateRequest {
  organization_id: string;
  reply_to_email?: string;
  reply_to_name?: string;
}

/**
 * Set up an organization for email campaigns using SendGrid
 */
export const setupOrganization = async (data: OrganizationSetupRequest) => {
  try {
    const response = await remiApi.post('/api/v1/setup-org', {
      organization_id: data.organization_id,
      custom_email_domain: data.email_domain,
      email_domain: data.email_domain,
      reply_to_email: data.reply_to_email,
      reply_to_name: data.reply_to_name,
    });
    return assertSetupSucceeded(response.data);
  } catch (error) {
    console.error('Error in setupOrganization:', error);
    throw new Error(getApiErrorMessage(error, 'Failed to set up organization email domain'));
  }
};

/**
 * Validate DNS configuration for organization's email domain
 */
export const validateDns = async (organizationId: string) => {
  try {
    const response = await remiApi.post('/api/v1/validate-dns', {
      organization_id: organizationId,
    });
    return response.data;
  } catch (error) {
    console.error('Error in validateDns:', error);
    throw error;
  }
};

/**
 * Get organization email setup status
 */
export const getOrgStatus = async (organizationId: string) => {
  try {
    const response = await remiApi.get(`/api/v1/org-status/${organizationId}`);
    return response.data;
  } catch (error) {
    console.error('Error in getOrgStatus:', error);
    throw error;
  }
};

/**
 * Get DNS records for an organization
 */
export const getDnsRecords = async (organizationId: string) => {
  try {
    const response = await remiApi.get(`/api/v1/dns-records/${organizationId}`);
    return response.data;
  } catch (error) {
    console.error('Error in getDnsRecords:', error);
    throw error;
  }
};

/**
 * Update organization email settings
 */
export const updateOrganization = async (data: OrganizationUpdateRequest) => {
  try {
    const response = await remiApi.post('/api/v1/update-sender', {
      organization_id: data.organization_id,
      reply_to_email: data.reply_to_email,
      reply_to_name: data.reply_to_name,
    });
    return response.data;
  } catch (error) {
    console.error('Error in updateOrganization:', error);
    throw error;
  }
};

/**
 * Re-authenticate domain for an organization
 */
export const reauthenticateDomain = async (organizationId: string) => {
  try {
    const response = await remiApi.post('/api/v1/authenticate-domain', {
      organization_id: organizationId,
    });
    return response.data;
  } catch (error) {
    console.error('Error in reauthenticateDomain:', error);
    throw error;
  }
};

/**
 * Check service health
 */
export const checkHealth = async () => {
  try {
    const response = await remiApi.get('/api/v1/health');
    return response.data;
  } catch (error) {
    console.error('Error in checkHealth:', error);
    throw error;
  }
};

/**
 * Change domain for an organization
 */
export const changeDomain = async (data: OrganizationSetupRequest) => {
  try {
    const response = await remiApi.post('/api/v1/setup-org', {
      organization_id: data.organization_id,
      custom_email_domain: data.email_domain,
      email_domain: data.email_domain,
    });
    return assertSetupSucceeded(response.data);
  } catch (error) {
    console.error('Error in changeDomain:', error);
    throw new Error(getApiErrorMessage(error, 'Failed to change organization email domain'));
  }
};
